// Obliczenia dla raportu "Magazyn Warszawa - czasy obsługi".
// Przyjazd auta, start i zakończenie skanowania to w bazie sama godzina "HH:mm"
// (data = data utworzenia zgłoszenia); podjazd i wyjazd auta to "YYYY-MM-DD HH:mm".
// Wszystko parsujemy jako UTC, bo liczą się tylko różnice między znacznikami.
import { kwantyl, mediana } from "../RaportCzasow/raportCzasowUtils";

export { formatCzas, formatData } from "../RaportCzasow/raportCzasowUtils";
export { kwantyl, mediana };

export const WSZYSTKIE = "__wszystkie__";

// Filtr "odprawa celna".
export const CELNA_FILTRY = [
  { klucz: WSZYSTKIE, etykieta: "Wszystkie" },
  { klucz: "nie", etykieta: "Bez odprawy celnej" },
  { klucz: "tak", etykieta: "Z odprawą celną" },
];

// Kolejność statusów Nowego Handlingu (jak w tabeli Magazynu Warszawa).
export const STATUSY_NH = [
  "Przyjęto na magazyn",
  "W odprawie celnej",
  "W oczekiwaniu na clenie",
  "Oclono",
  "Towar do wydania",
  "Zrealizowano",
];

const KOLORY_STATUSOW = {
  "Przyjęto na magazyn": "#3B6FB6",
  "W odprawie celnej": "#D08A2E",
  "W oczekiwaniu na clenie": "#C7544D",
  Oclono: "#8A63B8",
  "Towar do wydania": "#2E9C8F",
  Zrealizowano: "#6F7F90",
};
export const kolorStatusu = (s) => KOLORY_STATUSOW[s] || "#AEB6BF";

// Kolory etapów w jednej dostawie. Odprawa celna dostaje osobną, ciepłą rodzinę
// kolorów, bo jej czasu nie umiemy jeszcze mierzyć miarodajnie - na wykresach
// od razu widać, które dostawy są "odchyłem".
export const ETAPY = [
  {
    klucz: "oczekiwanie",
    nazwa: "Oczekiwanie na skanowanie",
    opis: "przyjazd auta → start skanowania",
    kolor: "#3B6FB6",
    kolorCelna: "#E0A458",
  },
  {
    klucz: "skanowanie",
    nazwa: "Skanowanie",
    opis: "start → zakończenie skanowania",
    kolor: "#2E9C8F",
    kolorCelna: "#D08A2E",
  },
  {
    klucz: "doPodjazdu",
    nazwa: "Do podjazdu pod wydanie",
    opis: "zakończenie skanowania → podjazd auta",
    kolor: "#8A63B8",
    kolorCelna: "#B8691F",
  },
  {
    klucz: "wydanie",
    nazwa: "Wydanie towaru",
    opis: "podjazd auta → wyjazd auta",
    kolor: "#6F7F90",
    kolorCelna: "#8F4A12",
  },
];

export const KOLOR_CELNA = "#D08A2E";
export const KOLOR_BEZ_CELNA = "#3B6FB6";

const toMs = (s) => (s ? Date.parse(String(s).replace(" ", "T") + "Z") : null);

// "HH:mm" + data -> ms. Godzina bez poprawnego formatu -> null.
const godzinaMs = (hhmm, data) => {
  const m = String(hhmm ?? "").trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m || !data) return null;
  return Date.parse(`${data}T${m[1].padStart(2, "0")}:${m[2]}:00Z`);
};

// Podjazd / wyjazd: pełna data, a gdy w bazie została sama godzina - data utworzenia.
const dataGodzinaMs = (value, dataUtworzenia) => {
  const v = String(value ?? "").trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return toMs(v.length === 16 ? `${v}:00` : v);
  return godzinaMs(v, dataUtworzenia);
};

const minuty = (a, b) => {
  if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) return null;
  const m = Math.round((b - a) / 6000) / 10;
  return m < 0 ? null : m;
};

const msNaTekst = (ms) =>
  ms == null ? null : new Date(ms).toISOString().slice(0, 16).replace("T", " ");

// Łączy zgłoszenia z historią statusów i liczy czasy etapów.
export function zbudujZgloszenia(zgloszenia, zmiany) {
  const poId = new Map();
  for (const z of zmiany) {
    if (!poId.has(z.customId)) poId.set(z.customId, []);
    poId.get(z.customId).push(z);
  }

  return zgloszenia.map((z) => {
    const data = String(z.utworzono || "").slice(0, 10);
    const auto = godzinaMs(z.przyjazdAuta, data);
    const start = godzinaMs(z.startSkanowania, data);
    const koniec = godzinaMs(z.zakonczenieSkanowania, data);
    const podjazd = dataGodzinaMs(z.podjazdAuta, data);
    const wyjazd = dataGodzinaMs(z.wyjazdAuta, data);

    const etapy = {
      oczekiwanie: minuty(auto, start),
      skanowanie: minuty(start, koniec),
      doPodjazdu: minuty(koniec, podjazd),
      wydanie: minuty(podjazd, wyjazd),
    };

    const historia = poId.get(z.id) || [];
    const statusy = historia.filter((h) => h.fieldName === "statusRealizacji");
    const pobyty = [];
    let od = z.utworzono;
    for (const h of statusy) {
      pobyty.push({
        status: h.oldValue || "—",
        nastepny: h.newValue,
        od,
        do: h.editedAt,
        minuty: minuty(toMs(od), toMs(h.editedAt)) ?? 0,
        kto: h.editedBy,
      });
      od = h.editedAt;
    }

    return {
      ...z,
      uzytkownik: z.createdBy || "—",
      celna: z.towarCelny === "Tak",
      auto,
      start,
      koniec,
      podjazd,
      wyjazd,
      etapy,
      minOczekiwanie: etapy.oczekiwanie,
      minSkanowanie: etapy.skanowanie,
      minWydanie: etapy.wydanie,
      // Jak długo paczka leży na magazynie: start skanowania → wyjazd auta.
      minNaMagazynie: minuty(start, wyjazd),
      minCalosc: minuty(auto, wyjazd),
      sumaEtapow: Object.values(etapy).reduce((s, v) => s + (v || 0), 0),
      znaczniki: {
        auto: msNaTekst(auto),
        start: msNaTekst(start),
        koniec: msNaTekst(koniec),
        podjazd: msNaTekst(podjazd),
        wyjazd: msNaTekst(wyjazd),
      },
      historia,
      pobyty,
    };
  });
}

export function filtruj(lista, { uzytkownicy, celna }) {
  return lista.filter((z) => {
    if (uzytkownicy.length && !uzytkownicy.includes(z.uzytkownik)) return false;
    if (celna === "tak" && !z.celna) return false;
    if (celna === "nie" && z.celna) return false;
    return true;
  });
}

// Podsumowanie: mediany i liczby.
export function podsumuj(lista) {
  const oczekiwanie = lista.map((z) => z.minOczekiwanie).filter((v) => v != null);
  const wydanie = lista.map((z) => z.minWydanie).filter((v) => v != null);
  const skanowanie = lista.map((z) => z.minSkanowanie).filter((v) => v != null);
  const naMagazynie = lista.map((z) => z.minNaMagazynie).filter((v) => v != null);
  return {
    liczba: lista.length,
    celnych: lista.filter((z) => z.celna).length,
    oczekiwanie: mediana(oczekiwanie),
    oczekiwanieP90: kwantyl(oczekiwanie, 0.9),
    oczekiwanieLiczba: oczekiwanie.length,
    wydanie: mediana(wydanie),
    wydanieP90: kwantyl(wydanie, 0.9),
    wydanieLiczba: wydanie.length,
    skanowanie: mediana(skanowanie),
    skanowanieP90: kwantyl(skanowanie, 0.9),
    skanowanieLiczba: skanowanie.length,
    naMagazynie: mediana(naMagazynie),
    naMagazynieP90: kwantyl(naMagazynie, 0.9),
    naMagazynieLiczba: naMagazynie.length,
  };
}

// Statystyki per użytkownik (createdBy): mediana oczekiwania na skanowanie i wydania.
export function statystykiUzytkownikow(lista) {
  const poUserze = new Map();
  for (const z of lista) {
    if (!poUserze.has(z.uzytkownik)) poUserze.set(z.uzytkownik, []);
    poUserze.get(z.uzytkownik).push(z);
  }
  return [...poUserze.entries()]
    .map(([uzytkownik, zgl]) => {
      const p = podsumuj(zgl);
      return { uzytkownik, ...p };
    })
    .sort((a, b) => b.liczba - a.liczba || a.uzytkownik.localeCompare(b.uzytkownik, "pl"));
}
